import { getQueryParams, QueryParams } from "types/queryParams.types";
import { baseApi } from "./baseApi";
import { TAG_TYPES, FormatResponse } from "types";
import { DriverRecord, CreateDriverPayload, UpdateDriverPayload } from "types/driver.type";

const URL_DRIVER = "admin/drivers";
const type = TAG_TYPES.DRIVER;

const driverApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all drivers
    getDriverAll: builder.query<
      FormatResponse<{ count: number; rows: DriverRecord[] }>,
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === "") return { url: URL_DRIVER, method: "GET" };
        return {
          url: `${URL_DRIVER}?${query}`,
          method: "GET",
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({
                type: type,
                id,
              })),
              { type: type, id: "LIST" },
            ]
          : [{ type: type, id: "LIST" }];
      },
    }),

    // Get driver by ID
    getDriverById: builder.query<
      FormatResponse<DriverRecord>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${URL_DRIVER}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [{ type: type, id }],
    }),

    // Create driver
    createDriver: builder.mutation<
      FormatResponse<DriverRecord>,
      CreateDriverPayload
    >({
      query: (data) => ({
        url: URL_DRIVER,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: type, id: "LIST" }],
    }),

    // Update driver
    updateDriver: builder.mutation<
      FormatResponse<DriverRecord>,
      UpdateDriverPayload
    >({
      query: ({ id, ...data }) => ({
        url: `${URL_DRIVER}/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: type, id },
        { type: type, id: "LIST" },
      ],
    }),

    // Delete driver
    deleteDriver: builder.mutation<
      FormatResponse<{ success: boolean }>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${URL_DRIVER}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: type, id },
        { type: type, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetDriverAllQuery,
  useGetDriverByIdQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
} = driverApiService;
